/**
 * IceDetailsDialog
 *
 * The popup behind the ICE badge on a scope row: the three scores, the
 * reasoning and evidence under each, the weakest dimension, the next step,
 * and what moved since the last evaluation.
 *
 * Read-only by design. Evaluating is a deliberate, paid action and lives in
 * the scope row's ⋯ menu — never behind a card the founder opened to read.
 *
 * @module @ideoz/ui/components/organisms/IceDetailsDialog
 */

import React from 'react';
import { Loader2, TriangleAlert } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../atoms/dialog';
import { Skeleton } from '../atoms/skeleton';
import { IceReasoningCard } from '../molecules/IceReasoningCard';
import { cn } from '../../lib/utils';
import type { IceDetailsDialogProps } from '../../types/Ice.types';

export const IceDetailsDialog: React.FC<IceDetailsDialogProps> = ({
  open,
  onClose,
  scopeTitle,
  data = null,
  isLoading = false,
  isScoring = false,
  error = null,
  className = '',
  testId = 'ice-details-dialog',
}) => {
  const card = data?.card ?? null;
  // A run with no card yet is a first evaluation; with one, it is a re-run
  // layered over the score already on file, which stays valid until the new
  // one lands.
  const isFirstRun = isScoring && !card;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent data-testid={testId} className={cn('max-h-[85vh] overflow-y-auto', className)}>
        <DialogHeader className="flex flex-col gap-1">
          <DialogTitle>ICE score</DialogTitle>
          <DialogDescription>
            {scopeTitle
              ? `${scopeTitle} — how this score was reached.`
              : 'How this score was reached.'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p
            className="flex items-start gap-1.5 rounded-lg border border-error-200 bg-error-25 p-2.5 leading-snug text-error-700"
            data-testid="ice-details-error"
          >
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
            <span>{error}</span>
          </p>
        )}

        {isLoading && !card && (
          <div className="flex flex-col gap-2" data-testid="ice-details-loading">
            <Skeleton className="h-16 w-full rounded-xl" />
            <Skeleton className="h-20 w-full rounded-lg" />
            <Skeleton className="h-20 w-full rounded-lg" />
          </div>
        )}

        {/* A first evaluation runs three agents sequentially — this is a
            loading state, not an empty one. */}
        {isFirstRun && !isLoading && (
          <div
            className="rounded-xl border border-gray-200 bg-gray-25 p-4"
            data-testid="ice-details-first-run"
          >
            <p className="flex items-center gap-1.5 text-gray-600" role="status">
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" strokeWidth={2} />
              Scoring this scope — this takes a few minutes.
            </p>
            <p className="mt-1.5 text-xs leading-snug text-gray-400">
              Three passes run over the evidence on file. You can close this and keep working; the
              score appears on the scope row when it lands.
            </p>
          </div>
        )}

        {isScoring && card && (
          <p
            className="flex items-center gap-1.5 text-gray-500"
            role="status"
            data-testid="ice-details-rescoring"
          >
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" strokeWidth={2} />
            Re-evaluating — showing the score on file.
          </p>
        )}

        {card && (
          <IceReasoningCard
            card={card}
            band={data?.current?.band ?? null}
            className={cn(isScoring && 'opacity-60 transition-opacity')}
          />
        )}

        {!card && !isFirstRun && !isLoading && !error && (
          <div
            className="rounded-xl border border-dashed border-gray-200 p-4 text-center"
            data-testid="ice-details-empty"
          >
            <p className="leading-snug text-gray-500">
              This scope has no ICE score yet. Use <span className="font-medium">Evaluate</span> in
              the scope&apos;s ⋯ menu — best once any files you uploaded have finished processing,
              since a score computed before then reads as low confidence no matter how good the idea
              is.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IceDetailsDialog;
